import React, { useRef, useMemo, useEffect, useState } from 'react';
import readingTime from 'reading-time/lib/reading-time';
import parse from 'html-react-parser';
import { Header, VimeoButton, VimeoVideo, References } from './index';
import { useWipAuthors } from '../../../../hooks/use-wip-authors';
import { getPostAuthors } from '../../../../model/author';
import { convertNumberToRoman } from '../../../../model/post';
import AudioIcon from '../../../../assets/svgs/audio.svg';
import TwitterIcon from '../../../../assets/svgs/twitter.svg';
import EmailIcon from '../../../../assets/svgs/email.svg';
import LogoIcon from '../../../../assets/svgs/logo-icon.svg';
import TranslateIcon from '../../../../assets/svgs/translate.svg';
import ArrowDown from '../../../../assets/svgs/arrow-down.svg';

const SingleArticle = ({
  postData,
  articleContent,
  selectedTextContent,
  referenceCount,
  referenceRowSizesDesktop,
  referenceRowSizesMobile,
  referencesAreaMeasureRef,
  referenceSidebarRefs,
  optionsArticleWordsPerMinute,
  socialUrls,
  handleCopyClick,
  isCopied,
  selectedClientRect,
  isHighlighting,
  showVideo,
  onPlayVideo,
  onCloseVideo,
}) => {
  const {
    // Graph powered
    title,
    date,
    excerpt,
    content,
    tghpwipArticleSpotlighted: articleSpotlighted,
    tghpwipArticleAudio: audioUrl,
    tghpwipArticleAuthor: authorIds,
    tghpwipArticleVideo: videoCode,
    tghpwipArticleFooterAuthorDetails: authorDetails,
    tghpwipArticleFooterEndnotes: endnotes,
    tghpwipReferences: references,
    // Bolted
    postImage,
    issueName,
    validCategories,
  } = postData;

  const isSpotlight = !!Number(articleSpotlighted);
  const allAuthors = useWipAuthors();
  const articleAuthors = getPostAuthors(authorIds, allAuthors);

  const wordsPerMinute = optionsArticleWordsPerMinute ? Number(optionsArticleWordsPerMinute) : 200;
  const articleReading = content ? readingTime(content, { wordsPerMinute: wordsPerMinute }) : 0;
  const articleReadingTime = content ? Math.ceil(articleReading.minutes) : '';

  const shareTextContentVisible = !!selectedTextContent && !isHighlighting;

  const excerptStripped = excerpt.replace(/<\/?[^>]+(>|$)/g, "");

  /**
   * Sharing
   */
  const shareTextContentRef = useRef(null);
  const referenceColumnRef = useRef(null);
  const [shareTop, shareBottom] = useMemo(() => {
    if (!referenceColumnRef.current || !shareTextContentRef.current || !selectedClientRect) {
      return [0, null];
    }

    const shareTextContentHeight = shareTextContentRef.current.offsetHeight;
    const top = selectedClientRect.top - referenceColumnRef.current.getBoundingClientRect().top;

    if (top + shareTextContentHeight > referenceColumnRef.current.offsetHeight) {
      return [null, 0];
    } else {
      return [top, null];
    }
  }, [referenceColumnRef, shareTextContentRef, selectedClientRect]);

  /**
   * Google translate
   */
  const [showLanguageList, setShowLanguageList] = useState(false);
  const languageListRef = useRef('');
  const googleListRef = useRef('');

  const googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement(
      {
        includedLanguages: 'en,fr,es,pt,de,uk,zh-CN,ja,hi,bn,ar',
      },
      'google-translate-element',
    );
  };

  useEffect(() => {
    if (
      showLanguageList &&
      !document.querySelector('script[src*="translate.google.com/translate_a"]')
    ) {
      let addScript = document.createElement('script');
      addScript.setAttribute(
        'src',
        '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit',
      );
      document.body.appendChild(addScript);
      window.googleTranslateElementInit = googleTranslateElementInit;
    }
  }, [showLanguageList]);

  const onChangeLanguage = (e) => {
    const activeClass = e.currentTarget.querySelector('button.button-active');

    activeClass && activeClass.classList.remove('button-active');
    e.target.classList.add('button-active');

    const lang = e.target.dataset.lang;
    const selectField = googleListRef.current.querySelector(`.goog-te-combo`);
    for (let i = 0; i < selectField.children.length; i++) {
      let option = selectField.children[i];
      // find language and change the former language of the hidden selection-field
      if (option.value === lang) {
        selectField.selectedIndex = i;
        selectField.dispatchEvent(new Event('change'));
        break;
      }
    }
  };

  const shareTextContentInnerHTML = (
    <>
      <p>Share this quote</p>
      <ul>
        <li>
          <a href={socialUrls.twitterShareUrl} target="_blank" rel="noopener noreferrer">
            <TwitterIcon />
          </a>
        </li>
        <li>
          <a href={socialUrls.emailShareUrl} target="_blank" rel="noopener noreferrer">
            <EmailIcon />
          </a>
        </li>
      </ul>
    </>
  );

  return (
    <>
      <Header
        title={title}
        date={date}
        issueName={issueName}
        postImage={postImage}
        categories={validCategories}
        isSpotlight={isSpotlight}
        articleReadingTime={articleReadingTime}
        postAuthors={articleAuthors}
        {...socialUrls}
        handleCopyClick={handleCopyClick}
        isCopied={isCopied}
      />
      <section className="single-article__content-grid">
        <div className="single-article__content-grid-inner">
          {excerptStripped && <h2 className="single-article__excerpt">{parse(excerptStripped)}</h2>}
          <aside className="single-article__toc"></aside>
          <article className="single-article__article">{articleContent}</article>
          <aside className="single-article__reference-column" ref={referenceColumnRef}>
            <div className="single-article__media">
              {audioUrl && (
                <a href={audioUrl} className="single-article__media-audio" target={`_blank`}>
                  <AudioIcon /> Listen
                </a>
              )}
              {videoCode && (
                <VimeoButton
                  buttonClassName="single-article__media-video"
                  buttonText="Watch alongside"
                  showIcon={true}
                  onPlay={onPlayVideo}
                />
              )}

              {/**
               * Google translate (NOT IN USE)
               */}
              <button
                className={`single-article__media-translate${
                  showLanguageList ? ' show-dropdown' : ''
                }`}
                onClick={() => setShowLanguageList(!showLanguageList)}
              >
                <TranslateIcon /> Translate{' '}
                <ArrowDown className="single-article__media-translate-arrow" />
              </button>
              <div id="google-translate-element" ref={googleListRef} />
              {showLanguageList && (
                <div
                  className="single-article__media-languages"
                  ref={languageListRef}
                  onClick={onChangeLanguage}
                  onKeyDown={onChangeLanguage}
                  role="button"
                  tabIndex="0"
                >
                  <button data-lang={'ar'} translate="no">
                    Arabic
                  </button>
                  <button data-lang={'bn'} translate="no">
                    Bengali
                  </button>
                  <button data-lang={'en'} translate="no" className={`button-active`}>
                    English
                  </button>
                  <button data-lang={'fr'} translate="no">
                    French
                  </button>
                  <button data-lang={'de'} translate="no">
                    German
                  </button>
                  <button data-lang={'hi'} translate="no">
                    Hindi
                  </button>
                  <button data-lang={'ja'} translate="no">
                    Japanese
                  </button>
                  <button data-lang={'zh-CN'} translate="no">
                    Mandarin
                  </button>
                  <button data-lang={'pt'} translate="no">
                    Portuguese
                  </button>
                  <button data-lang={'es'} translate="no">
                    Spanish
                  </button>
                </div>
              )}
            </div>
            <div
              className="single-article__content-share-snippet single-article__content-share-snippet--mobile"
              style={{
                opacity: shareTextContentVisible ? 1 : 0,
                zIndex: shareTextContentVisible ? 10 : -1,
              }}
              ref={shareTextContentRef}
            >
              {shareTextContentInnerHTML}
            </div>
            <div
              className="single-article__content-share-snippet single-article__content-share-snippet--desktop"
              style={{
                top: typeof shareTop === 'number' ? `${shareTop}px` : 'auto',
                bottom: typeof shareBottom === 'number' ? `${shareBottom}px` : 'auto',
                opacity: shareTextContentVisible ? 1 : 0,
                zIndex: shareTextContentVisible ? 10 : -1,
              }}
              ref={shareTextContentRef}
            >
              {shareTextContentInnerHTML}
            </div>

            {!!referenceCount && (
              <References
                references={references}
                referenceRowSizes={[referenceRowSizesDesktop, referenceRowSizesMobile]}
                referencesAreaMeasureRef={referencesAreaMeasureRef}
                referenceSidebarRefs={referenceSidebarRefs}
              />
            )}
          </aside>
          <section className="single-article__article-footer">
            <div className="single-article__article-footer-separator">
              <LogoIcon />
            </div>
            {authorDetails && (
              <div className="single-article__article-footer-footnote">{parse(authorDetails)}</div>
            )}
            {endnotes && endnotes.length > 0 && (
              <>
                <div className="single-article__article-footer-endnotes-title">
                  <span>Endnotes</span>
                </div>
                <div className="single-article__article-endnotes">
                  {endnotes.map((note, idx) => {
                    const noteNumber = idx + 1;
                    return (
                      <div className="single-article__article-endnotes-note" key={noteNumber}>
                        <div className="single-article__article-endnotes-note-number">
                          {convertNumberToRoman(noteNumber)}
                        </div>
                        <div className="single-article__article-endnotes-note-number">{note}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {showVideo && videoCode && <VimeoVideo videoCode={videoCode} onClose={onCloseVideo} />}
        </div>
      </section>
    </>
  );
};

export default SingleArticle;
